import { D, dim, fmtYM } from './dates.ts';
import type { EvidenceClassification, InfaktKind, InfaktLine, InfaktTemplate, InternalInvoiceKind } from './types.ts';
import { r2 } from './vat.ts';

export const INFAKT_KIND_LABEL: Record<InfaktKind, string> = {
  invoice: 'Faktura do kontrahenta',
  internal_invoice: 'Faktura wewnętrzna',
  internal_evidence: 'Dowód wewnętrzny',
};
/** Endpointy inFakt API v3 dla rodzajów dokumentów */
export const INFAKT_ENDPOINT: Record<InfaktKind, string> = {
  invoice: 'invoices',
  internal_invoice: 'internal_invoices',
  internal_evidence: 'internal_evidences',
};

/** Klasyfikacja dowodu wewnętrznego – nazwy jak w inFakcie. */
export const EVIDENCE_CLASSIFICATION: Record<EvidenceClassification, string> = {
  salary: 'Pozostałe przychody',
  sold: 'Sprzedaż',
};

/** Rodzaj transakcji faktury wewnętrznej – nazwy jak w inFakcie. */
export const INTERNAL_INVOICE_KIND: Record<InternalInvoiceKind, string> = {
  nonaccounting_sales: 'Sprzedaż bezrachunkowa',
  free_transfer_of_goods: 'Nieodpłatne przekazanie towarów',
  inventory_for_vat: 'Spis z natury dla celów VAT',
  refund_cash_register_tax_relief: 'Zwrot ulgi na kasę fiskalną',
  car_purchase_from_eu: 'Zakup nowego środka transportu z innego kraju UE',
};

export const VAT_SYMBOLS = ['23', '8', '5', '0', 'zw', 'np'];
export const FLAT_RATES = ['17', '15', '14', '12', '10', '8.5', '5.5', '3', '2'];

export interface LineCalc {
  name: string;
  quantity: number;
  unitNet: number;
  net: number;
  vat: number;
  gross: number;
  flat: string;
  vatSymbol: string;
  /** Szacunkowy ryczałt od tej pozycji (przychód netto × stawka) */
  flatTax: number;
}

export const vatPercent = (s: string) => (/^\d+(\.\d+)?$/.test(s) ? Number(s) : 0);
export const flatPercent = (s: string) => Number(String(s).replace(',', '.')) || 0;

/**
 * Wartości pozycji za miesiąc. `values[i]` to to, co wpisujesz co miesiąc:
 * liczba godzin (variable = quantity) albo kwota (variable = amount, netto lub brutto wg priceMode).
 */
export function calcLines(lines: InfaktLine[], values: (number | null | undefined)[] = []): LineCalc[] {
  return lines.map((l, i) => {
    const v = values[i];
    let quantity = l.quantity ?? 1;
    let price = l.price ?? 0;
    if (l.variable === 'quantity' && v != null) quantity = v;
    if (l.variable === 'amount' && v != null) { price = v; quantity = 1; }
    const vp = vatPercent(l.vat);
    let net: number, vat: number, gross: number, unitNet: number;
    if (l.priceMode === 'gross') {
      gross = r2(price * quantity);
      net = r2(gross / (1 + vp / 100));
      vat = r2(gross - net);
      unitNet = quantity ? r2(net / quantity) : 0;
    } else {
      unitNet = r2(price);
      net = r2(unitNet * quantity);
      vat = r2((net * vp) / 100);
      gross = r2(net + vat);
    }
    return { name: l.name, quantity, unitNet, net, vat, gross, flat: l.flat, vatSymbol: l.vat, flatTax: r2((net * flatPercent(l.flat)) / 100) };
  });
}

export function sumLines(c: LineCalc[]) {
  return c.reduce((a, x) => ({ net: r2(a.net + x.net), vat: r2(a.vat + x.vat), gross: r2(a.gross + x.gross), flatTax: r2(a.flatTax + x.flatTax) }),
    { net: 0, vat: 0, gross: 0, flatTax: 0 });
}

export const monthEnd = (ym: string) => {
  const y = +ym.slice(0, 4), m = +ym.slice(5, 7);
  return D(y, m, dim(y, m));
};

/** Szablony z opisu działalności – do szybkiego startu. */
export function presetTemplates(): Record<string, InfaktTemplate> {
  return {
    'tpl-sprzedaz': {
      kind: 'internal_invoice', name: 'Sprzedaż bez faktur', active: true, transactionKind: 'nonaccounting_sales',
      notes: 'Sprzedaż w sklepie nieudokumentowana fakturami, za miesiąc.',
      lines: [
        { name: 'Sprzedaż bez faktur – ryczałt 8,5%', price: 0, priceMode: 'gross', vat: '23', flat: '8.5', variable: 'amount' },
        { name: 'Sprzedaż bez faktur – ryczałt 5,5%', price: 0, priceMode: 'gross', vat: '23', flat: '5.5', variable: 'amount' },
      ],
    },
    'tpl-nieodplatne': {
      kind: 'internal_evidence', name: 'Nieodpłatne świadczenia', active: true, classification: 'salary',
      lines: [{ name: 'Przychód z tytułu nieodpłatnych świadczeń', price: 0, priceMode: 'net', vat: 'np', flat: '3', variable: 'amount' }],
    },
    'tpl-klient-godziny': {
      kind: 'invoice', name: 'Faktura godzinowa', active: true, paymentDays: 14, paymentMethod: 'transfer',
      lines: [{ name: 'Usługi programistyczne', unit: 'godz.', quantity: 0, price: 0, priceMode: 'net', vat: '23', flat: '12', variable: 'quantity' }],
    },
    'tpl-klient-staly': {
      kind: 'invoice', name: 'Faktura miesięczna', active: true, paymentDays: 14, paymentMethod: 'transfer',
      lines: [{ name: 'Usługi', unit: 'usł.', quantity: 1, price: 0, priceMode: 'net', vat: '23', flat: '8.5', variable: 'amount' }],
    },
  };
}

export const docKey = (templateId: string, ym: string) => `${templateId}_${ym}`;

/** Pozycje z wartością za miesiąc – pozycje z zerem (np. brak sprzedaży z daną stawką) nie trafiają do dokumentu. */
export function billableLines(lines: InfaktLine[], values: (number | null | undefined)[] = []): { l: InfaktLine; c: LineCalc }[] {
  const calc = calcLines(lines, values);
  return lines.map((l, i) => ({ l, c: calc[i] })).filter(({ c }) => c.net !== 0 || c.gross !== 0);
}
export const docTitle = (t: InfaktTemplate, ym: string) => `${t.name} za ${fmtYM(ym)}`;
